import React, { createContext, useContext, useMemo, useState } from "react";
import type { PopularModelCard } from "../data/models";
import type { GenerationCardSettings } from "../types/generationCard";

type SelectedModelContextValue = {
	selectedModel: PopularModelCard | null;
	prompt: string;
	selectModel: (model: PopularModelCard) => void;
	setPrompt: (value: string) => void;
	clearSelectedModel: () => void;
	isGenerating: boolean;
	setIsGenerating: (v: boolean) => void;
	activeAgent: { title: string; iconUrl: string } | null;
	setActiveAgent: (agent: { title: string; iconUrl: string } | null) => void;
	// Одноразовый канал пресета параметров (home-карточка → /generate).
	// Выставляется в selectModel, потребляется и очищается в GenerateContext.
	pendingSettings: GenerationCardSettings | null;
	setPendingSettings: (s: GenerationCardSettings | null) => void;
	// Целевая модель пресета (значение из ?model=). Нужна GenerateContext, чтобы
	// применить pendingSettings ТОЛЬКО когда выбранная модель догналась до
	// целевой — иначе model-sync сброс дефолтов затрёт пресет (см. фикс там).
	pendingModelId: string | null;
	setPendingModelId: (id: string | null) => void;
};

const SelectedModelContext = createContext<SelectedModelContextValue | undefined>(
		undefined
);

function buildDefaultPrompt(model: PopularModelCard): string {
        if (model.defaultPrompt) return model.defaultPrompt;
        return `${model.title}\n\n${model.description}`;
}
function loadModel(): PopularModelCard | null {
	try {
		const saved = localStorage.getItem('selectedModel')
		if (saved) return JSON.parse(saved)
	} catch { /* ignore */ }
	return null
}

function loadPrompt(): string {
	return localStorage.getItem('selectedPrompt') || ''
}

export function SelectedModelProvider({
																				children,
																			}: {
	children: React.ReactNode;
}) {
	const [selectedModel, setSelectedModel] = useState<PopularModelCard | null>(loadModel);
	const [prompt, setPromptState] = useState<string>(loadPrompt);
	const [isGenerating, setIsGenerating] = useState(false);
	const [activeAgent, setActiveAgent] = useState<{ title: string; iconUrl: string } | null>(null);
	const [pendingSettings, setPendingSettings] = useState<GenerationCardSettings | null>(null);
	const [pendingModelId, setPendingModelId] = useState<string | null>(null);

	const setPrompt = (value: string) => {
		setPromptState(value)
		localStorage.setItem('selectedPrompt', value)
	}

	const value = useMemo<SelectedModelContextValue>(
			() => ({
				selectedModel,
				prompt,
				isGenerating,
				setIsGenerating,
				activeAgent,
				setActiveAgent,
				pendingSettings,
				setPendingSettings,
				pendingModelId,
				setPendingModelId,
				selectModel: (model) => {
					setSelectedModel(model);
					localStorage.setItem('selectedModel', JSON.stringify(model));
					const p = buildDefaultPrompt(model);
					setPromptState(p);
					localStorage.setItem('selectedPrompt', p);
					// Пресет параметров карточки — одноразово на /generate.
					setPendingSettings(model.settings ?? null);
					// Целевая модель (значение из ?model=): GenerateContext применит
					// пресет только когда selectedModel догонится до неё.
					setPendingModelId(model.targetModelId || null);
				},
				setPrompt,
				clearSelectedModel: () => {
					setSelectedModel(null);
					setPromptState("");
					localStorage.removeItem('selectedModel');
					localStorage.removeItem('selectedPrompt');
					setPendingSettings(null);
					setPendingModelId(null);
				},
			}),
			[selectedModel, prompt, isGenerating, activeAgent, pendingSettings, pendingModelId]
	);

	return (
			<SelectedModelContext.Provider value={value}>
				{children}
			</SelectedModelContext.Provider>
	);
}

export function useSelectedModel() {
	const ctx = useContext(SelectedModelContext);
	if (!ctx)
		throw new Error(
				"useSelectedModel must be used within <SelectedModelProvider />"
		);
	return ctx;
}